import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import {
  getGetOralExamRunStateQueryKey,
  useAbortOralExam,
  useAdvanceOralExamQuestion,
  useCompleteOralExamIfAbsent,
  useEndOralExam,
  useLeaveOralExamPreflight,
  useMarkOralExamPreflightLobbyReady,
  useMarkOralExamStudentReady,
  useRecordOralExamConsent,
  useAcknowledgeOralExamConduct,
  useRedrawOralExamQuestions,
  useRequestOralExamHelp,
  useResetOralExam,
  useResetOralExamTimer,
  useRepeatOralExamPreviousQuestion,
  useSignalOralExamStudentDone,
  useStartOralExam,
  useSubmitOralExamIdentityPhotos,
} from '@/common/api/endpoints/oral-exams/oral-exams';
import { postOralExamOrientationCompleted } from '@/student/module/utils/oralExamLounge';
import {
  openOralExamAnswerWindowPath,
  startOralExamSelfServicePath,
} from '@/common/utils/oralExam/practiceApi';
import type { OralExamRouteIds } from './useOralExamRunStatePoll';
import { unwrapOralExamRunState } from './useOralExamRunStatePoll';
import {
  fetchFreshBookingEtag,
  isStaleEtagError,
  useOralExamMutationErrorHandler,
} from './useOralExamMutationErrorHandler';

type IfMatchHeaders = { 'If-Match': string };

export type OralExamMutationBookingContext = OralExamRouteIds & {
  etag?: string | null;
};

function ifMatchHeaders(etag: string | null | undefined): IfMatchHeaders | undefined {
  if (!etag) return undefined;
  return { 'If-Match': etag };
}

function bookingRunStateQueryKey(routeIds: OralExamRouteIds): readonly unknown[] {
  return getGetOralExamRunStateQueryKey(
    routeIds.courseId,
    routeIds.moduleId,
    routeIds.taskId,
    routeIds.bookingId,
  );
}

function currentQuestionIdFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  stateQueryKey: readonly unknown[],
): string | undefined {
  const runState = unwrapOralExamRunState(queryClient.getQueryData(stateQueryKey));
  return runState?.currentQuestion?.questionId;
}

export function useOralExamMutations(
  ids: OralExamRouteIds,
  options: {
    etag: string | null;
    stateQueryKey: readonly unknown[];
    onAfterSuccess?: () => void;
  },
) {
  const queryClient = useQueryClient();
  const { handleStale, withStaleRetry, onMutationError } = useOralExamMutationErrorHandler({
    stateQueryKey: options.stateQueryKey,
  });

  /** Done/Next/reset/repeat: refresh on 409/412 but never resend (questionId may be stale). */
  const withoutStaleRetry = async <T>(fn: () => Promise<T>): Promise<T> => {
    try {
      return await fn();
    } catch (error) {
      const status = error instanceof AxiosError ? error.response?.status : undefined;
      if (status === 409 || status === 412) {
        await queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
      }
      onMutationError(error);
      throw error;
    }
  };

  const questionScopedBody = (retryQueryKey?: readonly unknown[]) => {
    const questionId = currentQuestionIdFromCache(
      queryClient,
      retryQueryKey ?? options.stateQueryKey,
    );
    return questionId ? { questionId } : {};
  };

  const applyRunStateMutationSuccess = (data: unknown) => {
    if (unwrapOralExamRunState(data)) {
      queryClient.setQueryData(options.stateQueryKey, data);
    }
    queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
  };
  const {
    courseId, moduleId, taskId, bookingId,
  } = ids;

  const startMutation = useStartOralExam({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
        options.onAfterSuccess?.();
      },
    },
  });

  const redrawMutation = useRedrawOralExamQuestions({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
      },
    },
  });

  const nextMutation = useAdvanceOralExamQuestion({
    mutation: {
      onSuccess: (data) => {
        applyRunStateMutationSuccess(data);
      },
    },
  });

  const endMutation = useEndOralExam({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
      },
    },
  });

  const completeIfAbsentMutation = useCompleteOralExamIfAbsent({
    mutation: {
      onSuccess: (data) => {
        applyRunStateMutationSuccess(data);
      },
    },
  });

  const abortMutation = useAbortOralExam({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
      },
    },
  });

  const resetMutation = useResetOralExam({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
        options.onAfterSuccess?.();
      },
    },
  });

  const resetTimerMutation = useResetOralExamTimer({
    mutation: {
      onSuccess: (data) => {
        applyRunStateMutationSuccess(data);
      },
    },
  });

  const repeatPreviousMutation = useRepeatOralExamPreviousQuestion({
    mutation: {
      onSuccess: (data) => {
        applyRunStateMutationSuccess(data);
      },
    },
  });

  const studentDoneMutation = useSignalOralExamStudentDone({
    mutation: {
      onSuccess: (data) => {
        applyRunStateMutationSuccess(data);
      },
    },
  });

  const markStudentReadyMutation = useMarkOralExamStudentReady({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
      },
    },
  });

  const requestHelpMutation = useRequestOralExamHelp({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
      },
    },
  });

  const recordConsentMutation = useRecordOralExamConsent({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
      },
    },
  });

  const acknowledgeConductMutation = useAcknowledgeOralExamConduct({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
      },
    },
  });

  const submitIdentityPhotosMutation = useSubmitOralExamIdentityPhotos({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
      },
    },
  });

  // Best-effort: the student is leaving anyway, so a failed reset must never
  // block navigation or raise a scary toast (hence no onMutationError here).
  const leavePreflightMutation = useLeaveOralExamPreflight({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
      },
    },
  });

  const markPreflightLobbyReadyMutation = useMarkOralExamPreflightLobbyReady({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
      },
    },
  });

  const startSelfServiceMutation = useMutation({
    mutationFn: async (etag: string | null) => axios.post(
      startOralExamSelfServicePath({
        courseId, moduleId, taskId, bookingId,
      }),
      null,
      { headers: ifMatchHeaders(etag) },
    ),
    onSuccess: (response) => {
      applyRunStateMutationSuccess(response);
    },
  });

  const openAnswerWindowMutation = useMutation({
    mutationFn: async (etag: string | null) => axios.post(
      openOralExamAnswerWindowPath({
        courseId, moduleId, taskId, bookingId,
      }),
      null,
      { headers: ifMatchHeaders(etag) },
    ),
    onSuccess: (response) => {
      applyRunStateMutationSuccess(response);
    },
  });

  const base = {
    courseId, moduleId, taskId, bookingId,
  };
  const hookBookingQueryKey = useMemo(
    () => bookingRunStateQueryKey({
      courseId, moduleId, taskId, bookingId,
    }),
    [bookingId, courseId, moduleId, taskId],
  );

  const resolveBookingContext = (ctx?: OralExamMutationBookingContext) => ({
    courseId: ctx?.courseId ?? courseId,
    moduleId: ctx?.moduleId ?? moduleId,
    taskId: ctx?.taskId ?? taskId,
    bookingId: ctx?.bookingId ?? bookingId,
    etag: ctx?.etag !== undefined ? ctx.etag : options.etag,
  });

  // Error surfacing for mutations without If-Match retry is handled inside callers.

  return {
    startExam: () => withStaleRetry(
      (etag) => startMutation.mutateAsync({
        ...base,
        headers: ifMatchHeaders(etag),
      }),
      options.etag,
      hookBookingQueryKey,
    ),
    redrawQuestions: () => withStaleRetry(
      (etag) => redrawMutation.mutateAsync({
        ...base,
        headers: ifMatchHeaders(etag),
      }),
      options.etag,
      hookBookingQueryKey,
    ),
    // Lecturer "Next" / student Done / reset / repeat: always send the open
    // questionId from run state. No withStaleRetry — a 409 question_changed or
    // 412 etag mismatch means refresh and stop (Slice D2).
    advanceQuestion: (ctx?: { etag?: string | null }) => {
      const etag = ctx?.etag !== undefined ? ctx.etag : options.etag;
      return withoutStaleRetry(() => nextMutation.mutateAsync({
        ...base,
        data: questionScopedBody(hookBookingQueryKey),
        headers: ifMatchHeaders(etag),
      }));
    },
    endExam: (ctx?: { etag?: string | null }) => withStaleRetry(
      (etag) => endMutation.mutateAsync({
        ...base,
        headers: ifMatchHeaders(etag),
      }),
      ctx?.etag !== undefined ? ctx.etag : options.etag,
      hookBookingQueryKey,
    ),
    completeIfAbsent: (ctx?: { etag?: string | null }) => withStaleRetry(
      (etag) => completeIfAbsentMutation.mutateAsync({
        ...base,
        headers: ifMatchHeaders(etag),
      }),
      ctx?.etag !== undefined ? ctx.etag : options.etag,
      hookBookingQueryKey,
    ),
    // No ctx → aborts this hook's booking (student self-abort). With ctx, the
    // lecturer console removes a specific cohort student from their own tile.
    abortExam: (ctx?: OralExamMutationBookingContext) => {
      const resolved = resolveBookingContext(ctx);
      return withStaleRetry(
        (etag) => abortMutation.mutateAsync({
          courseId: resolved.courseId,
          moduleId: resolved.moduleId,
          taskId: resolved.taskId,
          bookingId: resolved.bookingId,
          headers: ifMatchHeaders(etag),
        }),
        resolved.etag,
        bookingRunStateQueryKey(resolved),
      );
    },
    // No ctx → resets this hook's booking (drop-in Run again). With ctx, the
    // lecturer console resets a specific cohort student from their tile.
    resetExam: (ctx?: OralExamMutationBookingContext) => {
      const resolved = resolveBookingContext(ctx);
      return withStaleRetry(
        (etag) => resetMutation.mutateAsync({
          courseId: resolved.courseId,
          moduleId: resolved.moduleId,
          taskId: resolved.taskId,
          bookingId: resolved.bookingId,
          headers: ifMatchHeaders(etag),
        }),
        resolved.etag,
        bookingRunStateQueryKey(resolved),
      );
    },
    resetTimer: (ctx?: OralExamMutationBookingContext) => {
      const resolved = resolveBookingContext(ctx);
      const stateKey = bookingRunStateQueryKey(resolved);
      return withoutStaleRetry(() => resetTimerMutation.mutateAsync({
        courseId: resolved.courseId,
        moduleId: resolved.moduleId,
        taskId: resolved.taskId,
        bookingId: resolved.bookingId,
        data: questionScopedBody(stateKey),
        headers: ifMatchHeaders(resolved.etag),
      }));
    },
    repeatPreviousQuestion: (ctx?: OralExamMutationBookingContext) => {
      const resolved = resolveBookingContext(ctx);
      const stateKey = bookingRunStateQueryKey(resolved);
      return withoutStaleRetry(() => repeatPreviousMutation.mutateAsync({
        courseId: resolved.courseId,
        moduleId: resolved.moduleId,
        taskId: resolved.taskId,
        bookingId: resolved.bookingId,
        data: questionScopedBody(stateKey),
        headers: ifMatchHeaders(resolved.etag),
      }));
    },
    // Done is a server-decided command: the open questionId is the only guard,
    // so a stale local etag must never block the student.
    signalStudentDone: () => withoutStaleRetry(() => studentDoneMutation.mutateAsync({
      ...base,
      data: questionScopedBody(hookBookingQueryKey),
    })),
    markStudentReady: () => withStaleRetry(
      (etag) => markStudentReadyMutation.mutateAsync({
        ...base,
        headers: ifMatchHeaders(etag),
      }),
      options.etag,
      hookBookingQueryKey,
    ),
    requestHelp: () => withStaleRetry(
      (etag) => requestHelpMutation.mutateAsync({
        ...base,
        headers: ifMatchHeaders(etag),
      }),
      options.etag,
      hookBookingQueryKey,
    ),
    recordConsent: (data: { policyVersion: string; scope: string }) => withStaleRetry(
      (etag) => recordConsentMutation.mutateAsync({
        ...base,
        data: { policyVersion: data.policyVersion, scope: data.scope },
        headers: ifMatchHeaders(etag),
      }),
      options.etag,
      hookBookingQueryKey,
    ),
    acknowledgeConduct: (data: { policyVersion: string }) => withStaleRetry(
      (etag) => acknowledgeConductMutation.mutateAsync({
        ...base,
        data: { policyVersion: data.policyVersion },
        headers: ifMatchHeaders(etag),
      }),
      options.etag,
      hookBookingQueryKey,
    ),
    submitIdentityPhotos: async (data: {
      documentImageBase64: string;
      selfieImageBase64: string;
      documentContentType?: string;
      selfieContentType?: string;
    }) => {
      const payload = {
        documentImageBase64: data.documentImageBase64,
        documentContentType: data.documentContentType ?? 'image/jpeg',
        selfieImageBase64: data.selfieImageBase64,
        selfieContentType: data.selfieContentType ?? 'image/jpeg',
      };
      const invoke = (etag: string | null | undefined) => submitIdentityPhotosMutation.mutateAsync({
        ...base,
        data: payload,
        headers: ifMatchHeaders(etag),
      });
      try {
        try {
          await invoke(options.etag);
        } catch (error) {
          if (!isStaleEtagError(error)) throw error;
          const freshEtag = await fetchFreshBookingEtag(queryClient, hookBookingQueryKey);
          await invoke(freshEtag);
        }
      } catch (error) {
        const status = error instanceof AxiosError ? error.response?.status : undefined;
        if (status === 409) {
          await queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
        }
        throw error;
      }
    },
    // Student intentionally leaves the pre-flight lobby: clears pending
    // consent/ID capture so the lecturer roster doesn't stay wedged. Best-effort
    // — always resolves so the caller can navigate away regardless.
    leavePreflight: () => leavePreflightMutation.mutateAsync(base).catch(() => undefined),
    // Best-effort: stamped when the student reaches the waiting room. Swallow
    // errors (no toast) but refresh run state on 412 so a later attempt can use
    // a fresh ETag — never loop with a stale If-Match.
    markPreflightLobbyReady: (ctx?: { etag?: string | null }) => {
      const etag = ctx?.etag !== undefined ? ctx.etag : options.etag;
      return markPreflightLobbyReadyMutation.mutateAsync({
        ...base,
        headers: ifMatchHeaders(etag),
      }).catch((error: unknown) => {
        const status = error instanceof AxiosError ? error.response?.status : undefined;
        if (status === 412) {
          queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
        }
        return undefined;
      });
    },
    markOrientationCompleted: () => withStaleRetry(
      async (matchEtag) => {
        const response = await postOralExamOrientationCompleted(base, matchEtag);
        queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
        return response.data;
      },
      options.etag,
      hookBookingQueryKey,
    ),
    startSelfServiceSession: () => withStaleRetry(
      (etag) => startSelfServiceMutation.mutateAsync(etag),
      options.etag,
      hookBookingQueryKey,
    ),
    openAnswerWindow: () => withStaleRetry(
      (etag) => openAnswerWindowMutation.mutateAsync(etag),
      options.etag,
      hookBookingQueryKey,
    ),
    isStarting: startMutation.isPending,
    isRedrawing: redrawMutation.isPending,
    isAdvancing: nextMutation.isPending,
    isEnding: endMutation.isPending,
    isCompletingIfAbsent: completeIfAbsentMutation.isPending,
    isAborting: abortMutation.isPending,
    isResetting: resetMutation.isPending,
    isResettingTimer: resetTimerMutation.isPending,
    isRepeatingPrevious: repeatPreviousMutation.isPending,
    isSignalingDone: studentDoneMutation.isPending,
    isMarkingStudentReady: markStudentReadyMutation.isPending,
    isRequestingHelp: requestHelpMutation.isPending,
    isRecordingConsent: recordConsentMutation.isPending,
    isAcknowledgingConduct: acknowledgeConductMutation.isPending,
    isSubmittingIdentityPhotos: submitIdentityPhotosMutation.isPending,
    isStartingSelfService: startSelfServiceMutation.isPending,
    isOpeningAnswerWindow: openAnswerWindowMutation.isPending,
    handleStale,
  };
}
